import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { LambdaService } from 'src/app/services/lambda.service';
import { InfobipReportAnalysisService } from './infobip-report-analysis.service';
import { InfobipReportExportService } from './infobip-report-export.service';
import {
  ExactDecimal,
  InfobipDataValidationError,
  InfobipFilterOptions,
  InfobipReportFilters,
  InfobipReportRecord,
  InfobipReportView
} from './infobip-report.models';

@Component({
  selector: 'app-infobip-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, ChartModule, DatePickerModule,
    ProgressSpinnerModule, SelectModule, TableModule, TabsModule, ToastModule],
  providers: [MessageService],
  templateUrl: './infobip-reports.component.html',
  styleUrl: './infobip-reports.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfobipReportsComponent implements OnInit {
  startDate: Date | null = null;
  endDate: Date | null = null;
  activeTab = 'dashboard';
  expandedClients: Record<string, boolean> = {};
  filters: InfobipReportFilters = this.emptyFilters();
  filterOptions: InfobipFilterOptions = this.emptyFilterOptions();
  sourceRecords: InfobipReportRecord[] = [];
  view: InfobipReportView;
  serverTotalRecords = 0;

  dailySpendChartData: Record<string, unknown> = { labels: [], datasets: [] };
  serviceSpendChartData: Record<string, unknown> = { labels: [], datasets: [] };
  clientSpendChartData: Record<string, unknown> = { labels: [], datasets: [] };
  lineChartOptions: Record<string, unknown> = {};
  barChartOptions: Record<string, unknown> = {};
  doughnutChartOptions: Record<string, unknown> = {};

  loading = false;
  exporting = false;
  hasLoaded = false;
  errorMessage = '';
  validationDetails: string[] = [];
  private appliedStartDate?: string;
  private appliedEndDate?: string;
  private requestSequence = 0;
  private readonly chartColors = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

  constructor(
    private readonly lambdaService: LambdaService,
    public readonly analysis: InfobipReportAnalysisService,
    private readonly exportService: InfobipReportExportService,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.view = this.analysis.buildView([], this.filters);
  }

  ngOnInit(): void {
    this.initializeChartOptions();
    void this.loadReports();
  }

  get hasResults(): boolean { return this.view.records.length > 0; }

  get canExport(): boolean {
    return this.hasResults && !this.loading && !this.exporting && !this.errorMessage
      && this.formatQueryDate(this.startDate) === this.appliedStartDate
      && this.formatQueryDate(this.endDate) === this.appliedEndDate;
  }

  async consultReports(): Promise<void> {
    if (this.isDateRangeValid()) await this.loadReports();
  }

  async refreshReports(): Promise<void> {
    if (this.isDateRangeValid()) await this.loadReports();
  }

  async clearFilters(): Promise<void> {
    this.startDate = null;
    this.endDate = null;
    this.filters = this.emptyFilters();
    await this.loadReports();
  }

  applyFilters(): void {
    this.view = this.analysis.buildView(this.sourceRecords, this.filters);
    this.expandedClients = {};
    this.buildChartData();
    this.cdr.markForCheck();
  }

  exportToExcel(): void {
    if (!this.canExport) return;
    this.exporting = true;
    this.cdr.markForCheck();
    try {
      this.exportService.download(this.view, {
        filters: { ...this.filters }, startDate: this.appliedStartDate,
        endDate: this.appliedEndDate, generatedAt: new Date()
      });
    } catch {
      this.showError('No se pudo generar el archivo Excel. Intenta nuevamente.');
    } finally {
      this.exporting = false;
      this.cdr.markForCheck();
    }
  }

  formatUsageDate(value: string): string {
    const day = value.slice(0, 10);
    const parts = day.split('-').map(Number);
    if (parts.length !== 3 || parts.some(part => !Number.isFinite(part))) return value;
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC'
    }).format(new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])));
  }

  formatMoney(value: ExactDecimal): string {
    return `$${this.analysis.formatDecimal(value, 2, 6)} USD`;
  }

  formatQuantity(value: ExactDecimal): string {
    return this.analysis.formatDecimal(value, 0, 6);
  }

  private async loadReports(): Promise<void> {
    const requestId = ++this.requestSequence;
    const startDate = this.formatQueryDate(this.startDate);
    const endDate = this.formatQueryDate(this.endDate);
    this.loading = true;
    this.errorMessage = '';
    this.validationDetails = [];
    this.cdr.markForCheck();
    try {
      const rawResponse = await this.lambdaService.getInfobipReports(startDate, endDate);
      if (requestId !== this.requestSequence) return;
      const normalized = this.analysis.normalizeResponse(rawResponse);
      this.sourceRecords = normalized.records;
      this.serverTotalRecords = normalized.totalRecords;
      this.filterOptions = this.analysis.buildFilterOptions(this.sourceRecords);
      this.resetUnavailableFilters();
      this.appliedStartDate = startDate;
      this.appliedEndDate = endDate;
      this.hasLoaded = true;
      this.applyFilters();
    } catch (error) {
      if (requestId !== this.requestSequence) return;
      this.sourceRecords = [];
      this.serverTotalRecords = 0;
      this.view = this.analysis.buildView([], this.filters);
      this.hasLoaded = true;
      if (error instanceof InfobipDataValidationError) {
        this.validationDetails = error.issues.slice(0, 5).map(issue =>
          `Registro ${issue.row}${issue.id !== undefined ? ` (id ${issue.id})` : ''}: ${issue.field} — ${issue.message}`);
        this.showError(`${error.message} No se calcularon totales ni se habilitó la exportación.`);
      } else {
        this.showError(error instanceof Error && error.message
          ? error.message : 'Ocurrió un error inesperado al consultar los reportes de Infobip.');
      }
      this.buildChartData();
    } finally {
      if (requestId === this.requestSequence) {
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  private resetUnavailableFilters(): void {
    const available = (options: Array<{ value: string | null }>, value: string | null) =>
      !value || options.some(option => option.value === value) ? value : null;
    this.filters = {
      account: available(this.filterOptions.accounts, this.filters.account),
      service: available(this.filterOptions.services, this.filters.service),
      trafficType: available(this.filterOptions.trafficTypes, this.filters.trafficType),
      direction: available(this.filterOptions.directions, this.filters.direction),
      country: available(this.filterOptions.countries, this.filters.country),
      operator: available(this.filterOptions.operators, this.filters.operator)
    };
  }

  private buildChartData(): void {
    this.dailySpendChartData = {
      labels: this.view.dailySpend.map(point => this.formatUsageDate(point.label)),
      datasets: [{ label: 'Facturación USD', data: this.view.dailySpend.map(point => this.analysis.toNumber(point.amount)),
        borderColor: '#2563eb', backgroundColor: '#2563eb33', fill: true, tension: 0.25 }]
    };
    this.serviceSpendChartData = {
      labels: this.view.serviceSpend.map(point => point.label),
      datasets: [{ data: this.view.serviceSpend.map(point => this.analysis.toNumber(point.amount)),
        backgroundColor: this.view.serviceSpend.map((_, index) => this.chartColors[index % this.chartColors.length]) }]
    };
    this.clientSpendChartData = {
      labels: this.view.clientSpend.map(point => point.label),
      datasets: [{ label: 'Facturación USD', data: this.view.clientSpend.map(point => this.analysis.toNumber(point.amount)), backgroundColor: '#10b981' }]
    };
  }

  private initializeChartOptions(): void {
    const scales = {
      y: { beginAtZero: true, ticks: { color: '#64748b' }, grid: { color: 'rgba(148, 163, 184, 0.18)' } },
      x: { ticks: { color: '#64748b' }, grid: { display: false } }
    };
    this.lineChartOptions = { responsive: true, maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'bottom' } }, scales };
    this.barChartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales };
    this.doughnutChartOptions = { responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom', labels: { usePointStyle: true } } } };
  }

  private isDateRangeValid(): boolean {
    const start = this.formatQueryDate(this.startDate);
    const end = this.formatQueryDate(this.endDate);
    if (start && end && start > end) {
      this.showError('La fecha inicial debe ser menor o igual a la fecha final.');
      return false;
    }
    return true;
  }

  private formatQueryDate(value: Date | null): string | undefined {
    if (!value) return undefined;
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.messageService.add({ severity: 'error', summary: 'Reportes Infobip', detail: message });
  }

  private emptyFilters(): InfobipReportFilters {
    return { account: null, service: null, trafficType: null, direction: null, country: null, operator: null };
  }

  private emptyFilterOptions(): InfobipFilterOptions {
    const option = (label: string) => [{ label, value: null }];
    return { accounts: option('Todos los clientes'), services: option('Todos los servicios'),
      trafficTypes: option('Todos los tipos'), directions: option('Todas las direcciones'),
      countries: option('Todos los países'), operators: option('Todos los operadores') };
  }
}
